import { z } from 'zod';
import { HydratedStory, ReducedTrail } from 'magical-trails';

const trailStoreKey = 'MAGICAL_TRAILS_TRAIL_STORE';
const progressStoreKey = 'MAGICAL_TRAILS_PROGRESS_STORE';
const analyticsConsentStoreKey = 'MAGICAL_TRAILS_COOKIE_CONSENT_STORE';
const entryAcknowledgedStoreKey = 'MAGICAL_TRAILS_ENTRY_ACK_STORE';

export interface TrailStore {
  [key: string]: ReducedTrail;
}

export interface ProgressStore {
  [key: string]: HydratedStory;
}

// The blobs these guards inspect come straight from JSON.parse on untrusted
// (player-editable, or just stale/legacy) localStorage — a syntactically
// valid parse can still hand back the wrong type for any individual field
// (`code: null`, `title: 42`, ...). readJsonStore only recovers from THROWS;
// these narrow individual values so a wrong-but-non-throwing shape can be
// skipped instead of silently producing a broken entry (e.g. `result[null]`
// coercing to the literal key `"null"`).
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

// Bump this whenever the MEANING of the stored analytics preference record
// changes. v1 was the old blocking accept/reject gate (`{ granted: boolean }`,
// opt-IN, analytics off until an explicit accept). v2 is the opt-OUT
// preference for the anonymous/cookieless model (`{ optedOut: boolean }`,
// analytics on by default) — note the field name AND its polarity are both
// inverted from v1. A record whose `version` isn't this exact value is never
// reinterpreted under the new field semantics; see `getAnalyticsOptedOut`
// below for how that's handled (deliberately NOT the same as "nothing
// stored").
export const ANALYTICS_CONSENT_VERSION = 2;

// Schema-validated, versioned, timestamped analytics opt-out preference.
const analyticsConsentRecordSchema = z.object({
  version: z.literal(ANALYTICS_CONSENT_VERSION),
  optedOut: z.boolean(),
  at: z.string(),
});

export type AnalyticsConsentRecord = z.infer<
  typeof analyticsConsentRecordSchema
>;

// Loose shape used only to recognise "this is SOME versioned record, just
// not the current version" (i.e. a real decision a player made under
// earlier terms) as distinct from "this key contains something that was
// never a real decision at all" (corrupt JSON, an unrelated object, etc).
const anyVersionedRecordShape = z.object({ version: z.number() }).passthrough();

/**
 * Safely read + JSON-parse a blob from localStorage. Returns null on a missing
 * key, unavailable storage (private mode / disabled), OR corrupt JSON — and
 * clears a corrupt key so the app self-heals instead of crash-looping.
 *
 * This MUST never throw: these reads run at MODULE-EVALUATION time — the Redux
 * trail slice seeds its initialState from getCodesInLocalStorage() /
 * getSavedStoriesProgress(), which run when `./store` is first imported, i.e.
 * before React (and therefore the <ErrorBoundary>) mounts. An unguarded
 * JSON.parse throw here white-screens the entire PWA with no recovery path
 * (the boundary can't catch a throw during synchronous import evaluation). So
 * every failure mode degrades to "no persisted data", never a throw.
 */
function readJsonStore<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn(
      `Could not read "${key}" from localStorage; clearing it.`,
      error
    );
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage itself is unavailable — nothing more we can do, just move on */
    }
    return null;
  }
}

export function getTrailStore(): TrailStore | null {
  return readJsonStore<TrailStore>(trailStoreKey);
}

export function getProgressStore(): ProgressStore | null {
  return readJsonStore<ProgressStore>(progressStoreKey);
}

export function getCodesInLocalStorage() {
  // Like readJsonStore above, this runs at MODULE-EVALUATION time (via the
  // trail slice's initialState) — before React/the ErrorBoundary mounts. A
  // syntactically-valid JSON blob whose SHAPE is wrong (e.g. a trail record
  // missing `points`/`stories` entirely, or holding something other than an
  // array) throws a TypeError on `.forEach` below, which readJsonStore's
  // try/catch can't catch since it already returned successfully. Guard here
  // too, and self-heal by clearing the offending key rather than
  // white-screening.
  try {
    const result: { [key: string]: string } = {};
    const trailStore = getTrailStore();
    if (trailStore) {
      Object.keys(trailStore).forEach((trail) => {
        // `.points`/`.stories` missing (or not arrays) still throws here on
        // purpose — that's a whole-record corruption, caught below and
        // self-healed by clearing the entire key. Individual malformed
        // ENTRIES inside an otherwise valid array (e.g. `{ code: null }`)
        // don't throw, so they're validated per-entry instead: silently
        // skipped rather than admitted with a bad key.
        trailStore[trail].points.forEach((point) => {
          if (point && isNonEmptyString((point as { code?: unknown }).code)) {
            result[point.code] = trail;
          }
        });
        trailStore[trail].stories.forEach((story) => {
          if (story && isNonEmptyString((story as { code?: unknown }).code)) {
            result[story.code] = trail;
          }
        });
      });
    }
    return result;
  } catch (error) {
    console.warn(
      `Could not read "${trailStoreKey}" from localStorage (unexpected shape); clearing it.`,
      error
    );
    clearTrailStore();
    return {};
  }
}

export function getSavedStoriesProgress() {
  // A wrong-shape (but valid JSON) record used to throw on `.filter`/`.length`
  // below, which must not white-screen the app at module-eval time. The
  // per-record and per-point guards below now validate shape defensively
  // instead of relying on a throw, so a malformed SIBLING record (or point)
  // is individually skipped rather than crashing the whole read — the outer
  // try/catch (self-healing by clearing the key) is kept only as a
  // last-resort net for anything unexpected that still slips through.
  try {
    const progressStore = getProgressStore();
    if (progressStore) {
      return (
        Object.keys(progressStore)
          .map((story) => progressStore[story])
          // A record missing its own metadata (e.g. `{ storyPoints: [] }`
          // with no title/code/startedAt) — or missing/mistyping
          // `storyPoints` itself — used to either throw (wiping every OTHER
          // saved trail's progress too) or silently produce an entry full of
          // `undefined`s. Drop those individually: one bad sibling record
          // must not lose everyone else's progress.
          .filter(
            (story) =>
              isNonEmptyString(story?.title) &&
              isNonEmptyString(story?.code) &&
              isNonEmptyString(story?.startedAt) &&
              Array.isArray(story?.storyPoints)
          )
          .map((story) => ({
            title: story.title,
            code: story.code,
            startedAt: story.startedAt,
            totalPoints: story.storyPoints.length,
            // Narrowed: `point.scanned` used to be read via `any` on the
            // assumption every array entry is a well-formed point object, so
            // a garbage entry (`null`, a primitive) would throw, and any
            // truthy-but-bogus value (e.g. a corrupt `"false"` string) on a
            // real one counted as scanned. A real scan always leaves a
            // parseable ISO timestamp on a `location` point (see
            // store/trail/index.ts's `setPointScanned`), so require exactly
            // that shape and skip anything else.
            scanedPoints: story.storyPoints.filter((point) => {
              if (!point || typeof point !== 'object') return false;
              const { type, scanned } = point as {
                type?: unknown;
                scanned?: unknown;
              };
              return (
                type === 'location' &&
                isNonEmptyString(scanned) &&
                !Number.isNaN(Date.parse(scanned))
              );
            }).length,
            version: story.version,
          }))
      );
    }
    return [];
  } catch (error) {
    console.warn(
      `Could not read "${progressStoreKey}" from localStorage (unexpected shape); clearing it.`,
      error
    );
    clearProgressStore();
    return [];
  }
}

export function getSavedStory(code: string) {
  const progressStore = getProgressStore();
  return progressStore?.[code];
}

export function saveTrail(trail: ReducedTrail) {
  // Called synchronously inside the loadTrail reducer. A QuotaExceededError (or
  // storage being unavailable, e.g. private mode) must not white-screen the
  // trail load — persistence is best-effort; the in-memory trail still plays.
  try {
    const trailStore = getTrailStore() || {};
    trailStore[trail.id] = trail;
    localStorage.setItem(trailStoreKey, JSON.stringify(trailStore));
  } catch (error) {
    console.warn('Could not persist trail to localStorage:', error);
  }
}

export function saveStory(story: HydratedStory) {
  try {
    const progressStore = getProgressStore() || {};
    progressStore[story.code] = story;
    localStorage.setItem(progressStoreKey, JSON.stringify(progressStore));
  } catch (error) {
    console.warn('Could not persist story progress to localStorage:', error);
  }
}

export function clearTrailStore() {
  localStorage.removeItem(trailStoreKey);
}

export function clearProgressStore() {
  localStorage.removeItem(progressStoreKey);
}

export function clearAnalyticsConsentStore() {
  localStorage.removeItem(analyticsConsentStoreKey);
}

export function clearEntryAcknowledgedStore() {
  localStorage.removeItem(entryAcknowledgedStoreKey);
}

/**
 * Reads + validates the stored analytics opt-out record. Returns null for a
 * missing key, unreadable storage, corrupt JSON, an unrecognised `version`,
 * or any other schema mismatch — a null return does NOT by itself tell you
 * whether analytics should be on or off; use `getAnalyticsOptedOut` for that.
 */
export function getAnalyticsConsentRecord(): AnalyticsConsentRecord | null {
  const raw = readJsonStore<unknown>(analyticsConsentStoreKey);
  if (raw === null) return null;

  const parsed = analyticsConsentRecordSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Is there a validated, CURRENT-version record on this key at all? */
export function hasCurrentAnalyticsConsentRecord(): boolean {
  return getAnalyticsConsentRecord() !== null;
}

/**
 * Should analytics be OFF right now, per whatever is on record? This is the
 * cookieless/anonymous model's fail-toward-privacy startup rule:
 *
 *  - A valid CURRENT-version record is authoritative — use its `optedOut`.
 *  - Nothing on record at all, or a blob that was never a real decision
 *    (corrupt JSON, an unrelated/garbage shape) → analytics stays ON. This
 *    is the cookieless default: session-scoped, anonymous aggregate events
 *    with no persistent identifier, so there's nothing here for a blocking
 *    gate to protect against.
 *  - A record from an OLD version (a REAL decision a player made, just
 *    under terms that have since changed — e.g. the pre-redesign
 *    accept/reject shape) → treated as opted OUT until the player
 *    re-confirms under the current terms, rather than being silently
 *    reinterpreted, or reset to the new "on" default.
 */
export function getAnalyticsOptedOut(): boolean {
  const raw = readJsonStore<unknown>(analyticsConsentStoreKey);
  if (raw === null) return false;

  const current = analyticsConsentRecordSchema.safeParse(raw);
  if (current.success) return current.data.optedOut;

  const old = anyVersionedRecordShape.safeParse(raw);
  if (old.success) {
    // A versioned record that failed the strict CURRENT-version parse above is
    // either from an OLD version OR a corrupt CURRENT-version record — both are
    // a real prior decision, so fail toward privacy (opted OUT) rather than
    // silently re-enrolling someone who had opted out. (Previously only the
    // old-version case fail-closed, so a field-corrupt current record re-turned
    // analytics ON.)
    return true;
  }

  return false; // never a real decision — cookieless default stays on
}

/**
 * Persists the player's analytics opt-out preference. Returns true on
 * success, false if localStorage threw (quota exceeded, private-mode
 * storage disabled, etc). Callers MUST check this return value: PostHog's
 * opt_in/opt_out_capturing() should already have been applied for the
 * current in-memory session regardless, but a failed persist means the
 * choice won't survive a reload — an opt-OUT that fails to save must not be
 * silently treated as "analytics re-enabled", it should surface to the
 * player instead.
 */
export function setAnalyticsOptOut(optedOut: boolean): boolean {
  try {
    const record: AnalyticsConsentRecord = {
      version: ANALYTICS_CONSENT_VERSION,
      optedOut,
      at: new Date().toISOString(),
    };
    localStorage.setItem(analyticsConsentStoreKey, JSON.stringify(record));
    return true;
  } catch (error) {
    console.warn('Could not persist analytics opt-out preference:', error);
    return false;
  }
}

// Schema-validated shape for the entry-acknowledgement record — guards
// against a stray/garbage value at this key (e.g. `{}`, `false`, `42`) being
// misread as an acknowledgement.
const entryAcknowledgedRecordSchema = z.object({
  at: z.iso.datetime(),
});

/**
 * Has the player dismissed the "Before you set off" entry screen at least
 * once (pressed the proceed button, acknowledging the safety notice)? This
 * is NOT a privacy/consent record — analytics is anonymous+cookieless and
 * never gated on it — it just avoids re-showing the same safety notice on
 * every visit. Best-effort: if this can't be persisted, the player simply
 * sees the notice again next time, which is harmless.
 */
export function hasAcknowledgedEntry(): boolean {
  const raw = readJsonStore<unknown>(entryAcknowledgedStoreKey);
  if (raw === null) return false;

  return entryAcknowledgedRecordSchema.safeParse(raw).success;
}

export function setEntryAcknowledged(): void {
  try {
    localStorage.setItem(
      entryAcknowledgedStoreKey,
      JSON.stringify({ at: new Date().toISOString() })
    );
  } catch (error) {
    console.warn('Could not persist entry acknowledgement:', error);
  }
}
